import { useTranslation } from "react-i18next";
import { Button, Input } from "../UI";
import styled, { useTheme } from "styled-components";
import { observer, inject } from "mobx-react";
import { useState } from "react";
import amount from "../asserts/img/credit-card.svg";
import descriptionImg from "../asserts/img/q.svg";
import { toBase64, isAcceptedFormat } from "../utils/helper";
import { inputsHasError, useInput } from "../utils/hooks";
import { isNotEmpty, withError } from "../validation";
import { ClipLoader } from "react-spinners";
import LoadingContainer from "../layouts/LoadingContainer";
import PageHeader from "../layouts/PageHeader";
import { useNavigate } from "react-router-dom";
import ExtentionInsert from "../components/ExtentionInsert";
import ExtentionPreview from "../components/ExtentionPreview";

const Error = styled("div")`
  margin-top: 1rem;
  color: red;
`;

const Expense = ({ monthReportsStore, userStore }) => {
  const { t } = useTranslation("month_reports");
  const accept = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
  const theme = useTheme();
  const [isSaving, setSaving] = useState(false);
  const [expense, setExpense] = useState(
    monthReportsStore.selectedExpense?.data || []
  );
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const isNone = (input) => {
    if (input === "none") return false;
    return true;
  };

  const isNotEmptyWithError = withError(
    isNotEmpty,
    t("error:text_field_empty_error")
  );

  const isNoneWithError = withError(isNone, t("error:text_field_empty_error"));

  const isNumericNonZero = withError(
    (input) => !isNaN(parseFloat(input)) && isFinite(input) && input > 0,
    t("error:text_field_numeric_non_zero_error")
  );

  const descriptionInput = useInput({
    validation: [isNotEmptyWithError],
    placeholder: t("description"),
    value: expense?.EXTFILEDESC || "",
  });

  const vatPriceInput = useInput({
    validation: [isNotEmptyWithError, isNoneWithError, isNumericNonZero],
    type: "number",
    placeholder: t("price"),
    value: expense?.VATPRICE?.toLocaleString() || "",
  });

  const onSave = async (data) => {
    monthReportsStore.selectedExpense?.EXTFILENUM !== undefined
      ? await monthReportsStore.updateExpense(data)
      : await monthReportsStore.addExpense(data);
  };

  const handleDescriptionChange = (e) => {
    setExpense({ ...expense, EXTFILEDESC: e.target.value });
  };

  const handleVatPriceChange = (e) => {
    setExpense({ ...expense, VATPRICE: e.target.value });
  };

  const handelNewExpense = async () => {
    if (isSaving) return;

    monthReportsStore.setError("");
    if (!inputsHasError(descriptionInput, vatPriceInput)) {
      const data = {
        EXTFILEDES: expense?.EXTFILEDESC,
        EXTFILENAME: expense?.extentions[0].EXTFILENAME,
        VATPRICE: parseFloat(expense?.VATPRICE),
      };
      delete data["extentions"];

      const body = {
        EXTFILENUM: monthReportsStore.selectedExpense?.EXTFILENUM,
        PERIOD: monthReportsStore.selectedExpense?.PERIOD,
        GL: monthReportsStore.selectedExpense?.GL,
        ZCR_USERID: userStore.userDetails?.ZCR_USERB,
        data: data,
      };
      setSaving(true);
      await onSave(body);
      setSaving(false);

      if (!!monthReportsStore.error) {
        setError(monthReportsStore.error);
      } else {
        navigate("/month_reports");
      }
    }
  };

  const handleSelectedFile = async (e) => {
    e.preventDefault();
    const files = e.target.files;
    isAcceptedFormat(files[0], accept);

    if (!isAcceptedFormat(files[0], accept)) {
      setError(t("error:format_file_type_not_allowed"));
      return;
    }

    if (files.length > 1) {
      setError("error:error_failed_upload_file");
      return;
    }

    try {
      const base64 = await toBase64(files[0]);
      const extentions = [];
      extentions.push({
        EXTFILEDES: files[0].name,
        EXTFILENAME: base64,
      });
      setExpense({
        ...expense,
        EXTFILENAME: extentions[0].EXTFILENAME,
        extentions,
      });
    } catch (err) {
      setError(t("error:error_failed_upload_file"));
    }
  };

  const removeSelectedFile = () => {
    const exp = expense;
    delete exp["EXTFILENAME"];
    delete exp["extentions"];
    exp.extentions = [];
    setExpense({ ...exp });
  };

  return (
    <LoadingContainer>
      <PageHeader
        title={
          monthReportsStore.selectedExpense?.data !== undefined
            ? t("update_expense_title")
            : t("new_expense_title")
        }
        back={() => navigate(-1)}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          flex: 1,
          gap: 20,
          width: "100%",
          padding: 20,
          height: "calc(100vh - 160px)",
        }}>
        <Input
          style={{ textAlign: "start", width: "100%" }}
          startImg={descriptionImg}
          {...descriptionInput}
          label={t("description")}
          onBlur={(e) => handleDescriptionChange(e)}
        />
        <Input
          style={{ textAlign: "start", width: "100%" }}
          {...vatPriceInput}
          startImg={amount}
          label={t("price")}
          onWheel={(e) => e.target.blur()}
          onBlur={(e) => handleVatPriceChange(e)}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 10,
          }}>
          <ExtentionInsert
            singleFile={true}
            extentions={expense.extentions}
            onAdd={handleSelectedFile}
          />
          {expense.extentions && (
            <ExtentionPreview
              onRemove={removeSelectedFile}
              files={expense.extentions}
            />
          )}
        </div>
        {!!error && <Error> {t(error)} </Error>}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "12px 20px",
          gap: 14,
        }}>
        <Button
          onClick={handelNewExpense}
          buttonStyle="main"
          style={{ marginTop: 30 }}>
          {isSaving ? (
            <div style={{ paddingTop: 6 }}>
              <ClipLoader color={theme.color.label} size={22} />
            </div>
          ) : monthReportsStore.selectedExpense?.data !== undefined ? (
            t("update_expense")
          ) : (
            t("add_expense")
          )}
        </Button>
        <Button
          onClick={() => navigate(-1)}
          buttonStyle="border-linear"
          style={{ marginTop: 30 }}>
          {t("common:cancel")}
        </Button>
      </div>
    </LoadingContainer>
  );
};

export default inject("monthReportsStore", "userStore")(observer(Expense));
